import * as router from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch } from "../../redux-store";
import { SocketContext } from "../../SocketConnection";
import {
  expandRow,
  loadRouteWithStops,
  loadRouteWithStopsUsingVersion,
  setShowCompletedDispatcherCreatedStops,
} from "../../ducks/data/route-with-stops";
import DriverCreatedCargos from "./DriverCreatedCargos/DriverCreatedCargos";
import { initialize } from "../../ducks/features";
import DispatcherCreatedStops from "./DispatcherCreatedCargos/DispatcherCreatedStops";
import { loadClients } from "../../ducks/data/clients";
import { loadHideLoadmeterState } from "../../ducks/data/driver-app-settings";
import withAuth from "../../components/withAuth";
import { RoutePageContextProvider } from "./RoutePageContext";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  selectErrorReason,
  selectInitialLoaded,
  selectReceivedDataInvalid,
  selectShowCompletedDispatcherStops,
  selectStopsAndWaypointsNumberNew,
  showHideCompletedStopsButtonNew,
} from "../../ducks/data/route-with-stops/selectors";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import AddNewCargoDialog from "../AddNewCargoPage";
import { selectMe } from "../../ducks/auth/selectors";
import { loadFinanceDataForDriver } from "../../ducks/data/finance";
import useFeature from "../../hooks/useFeature";
import { AddCargosFromUndispatchedDialog } from "./AddCargosFromUndispatchedDialog/AddCargosFromUndispatchedDialog";
import { loadCargoTypes, clearCargoTypes } from "../../ducks/app/cargo-types";
import { handleActionError } from "../../helpers/actionError";

const RoutePage = () => {
  const [showAddNewCargoDialog, setShowAddNewCargoDialog] = useState(false);
  const [showAddUndispatchedDialog, setShowAddUndispatchedDialog] =
    useState(false);
  const { routeId } = router.useParams();
  const { t } = useTranslation([
    "translation",
    "undispatchedCargos",
    "routeWithStops",
  ]);
  // TODO: outdated, remove
  const driverAddInvoiceLinesFeatureEnabled = useFeature(
    "driver-add-invoice-lines"
  );
  // TODO: outdated, remove
  const driverAddCargosToFromUndispatchedFeatureEnabled = useFeature(
    "drivers-add-undispatched-cargos"
  );
  // When on, route updates are driven by socket nudges instead of the interval
  // poll below (the two are mutually exclusive).
  const routeUpdatesViaWebsockets = useFeature(
    "mobile-app-route-updates-websockets"
  );
  const driverProfile = useSelector(selectMe);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (
      driverAddInvoiceLinesFeatureEnabled &&
      driverProfile?.canAddInvoiceLines
    ) {
      dispatch(loadFinanceDataForDriver());
    }
  }, [dispatch, driverProfile, driverAddInvoiceLinesFeatureEnabled]);

  useEffect(() => {
    dispatch(clearCargoTypes());
    dispatch(loadCargoTypes());
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadRouteWithStops(routeId as string));
    dispatch(loadClients());
    dispatch(loadHideLoadmeterState());
    dispatch(initialize());
  }, [dispatch, routeId]);

  useEffect(() => {
    // Socket nudges drive updates instead — skip polling entirely.
    if (routeUpdatesViaWebsockets) {
      return;
    }
    const interval = setInterval(() => {
      if (routeId) {
        dispatch(loadRouteWithStopsUsingVersion(routeId as string));
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [routeId, dispatch, routeUpdatesViaWebsockets]);

  const socket = useContext(SocketContext);

  // Live refetch trigger (used instead of polling when the websockets feature is
  // on): while viewing a route, join its room and refetch when the server
  // signals the version changed. Re-joining on reconnect (and refetching then)
  // reconciles any nudge missed while offline; the versioned fetch is a no-op
  // when nothing actually changed.
  useEffect(() => {
    if (!socket || !routeId || !routeUpdatesViaWebsockets) {
      return;
    }
    const refetch = () =>
      dispatch(loadRouteWithStopsUsingVersion(routeId as string));
    const joinAndReconcile = () => {
      socket.emit("join-driver-route", { routeId });
      refetch();
    };
    if (socket.connected) {
      joinAndReconcile();
    }
    socket.on("connect", joinAndReconcile);
    socket.on("driver-route.version-changed", refetch);
    return () => {
      socket.emit("leave-driver-route", { routeId });
      socket.off("connect", joinAndReconcile);
      socket.off("driver-route.version-changed", refetch);
    };
  }, [socket, routeId, dispatch, routeUpdatesViaWebsockets]);

  const errorReason = useSelector(selectErrorReason);

  const allRelevantRouteStopsNumber = useSelector(
    selectStopsAndWaypointsNumberNew
  );
  const showCompletedDispatcherCreatedStops = useSelector(
    selectShowCompletedDispatcherStops
  );
  const showHideCompletedStopsButton = useSelector(
    showHideCompletedStopsButtonNew
  );
  const initialLoaded = useSelector(selectInitialLoaded);

  const isFetchedDataIncompleteOrInvalid = useSelector(
    selectReceivedDataInvalid
  );

  if (!routeId || !driverProfile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <CircularProgress style={{ color: "black" }} size={44} />
      </Box>
    );
  }

  if (errorReason) {
    return <div>{t("routeWithStops:errorContactSupport")}</div>;
  }

  const onCloseAddNewModal = (result?: { cargoId: string }) => {
    setShowAddNewCargoDialog(false);
    dispatch(loadRouteWithStops(routeId))
      .unwrap()
      .then(() => {
        if (result) {
          dispatch(
            expandRow({ id: result.cargoId, type: "driverCreatedOrder" })
          );
        }
      })
      .catch((err) => {
        // The cargo itself was saved; only the refresh failed. The page keeps
        // showing the previous data and loadRouteWithStops doesn't raise the
        // error banner/toast, so just report it.
        handleActionError(err, {
          type: "Route reload after adding a cargo failed",
        });
      });
  };

  return (
    <RoutePageContextProvider>
      <div>
        {showAddNewCargoDialog && (
          <AddNewCargoDialog onClose={onCloseAddNewModal} />
        )}
        {showAddUndispatchedDialog &&
          driverProfile.canAddUndispatchedCargos && (
            <AddCargosFromUndispatchedDialog
              routeId={routeId}
              onClose={() => setShowAddUndispatchedDialog(false)}
            />
          )}
        {isFetchedDataIncompleteOrInvalid && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="rgba(128, 128, 128, 0.5)"
            zIndex={9999}
          >
            <CircularProgress style={{ color: "black" }} size={44} />
          </Box>
        )}
        <div
          style={{
            position: "fixed",
            bottom: "13vh",
            right: 30,
            background: "var(--primary-500)",
            borderRadius: "50%",
            zIndex: 1,
            boxShadow: "2px 2px 7px rgba(0, 0, 0, 0.8)",
          }}
        >
          <IconButton
            onClick={() => setShowAddNewCargoDialog(true)}
            style={{ color: "white" }}
          >
            <AddIcon style={{ fontSize: 30 }} />
          </IconButton>
        </div>
        <div className="flex items-start justify-between">
          <div>
            <Typography variant="h5" component="h1">
              {t("translation:MyRoute")}
            </Typography>
            <Typography variant="overline">
              {t("translation:AmountOfStops", {
                count: allRelevantRouteStopsNumber,
              })}
            </Typography>
          </div>
          {driverAddCargosToFromUndispatchedFeatureEnabled &&
            driverProfile.canAddUndispatchedCargos && (
              <div>
                <Button
                  variant="contained"
                  onClick={() => setShowAddUndispatchedDialog(true)}
                >
                  {t("undispatchedCargos:title")}
                </Button>
              </div>
            )}
        </div>
        <br />
        {showHideCompletedStopsButton && (
          <div className="my-1">
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => {
                dispatch(
                  setShowCompletedDispatcherCreatedStops(
                    !showCompletedDispatcherCreatedStops
                  )
                );
              }}
            >
              {showCompletedDispatcherCreatedStops
                ? t("startStopDialog.hideCompletedStops")
                : t("startStopDialog.showCompletedStops")}
            </Button>
          </div>
        )}
        <br />
        {!initialLoaded && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress style={{ color: "black" }} size={44} />
          </Box>
        )}
        <DispatcherCreatedStops />
        <br />
        <br />
        <DriverCreatedCargos />
      </div>
    </RoutePageContextProvider>
  );
};

export default withAuth(RoutePage);
